import { Outlet, Link } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown, Activity, ClipboardList, UserCheck, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ExitIntentPopup from "./ExitIntentPopup";
import KaiLauncher from "./KaiLauncher";

export const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);

  // Five-item nav: Logo · Start the System CTA · Resources dropdown · Community · Sign In
  const resourcesPrimary = [
    { name: "Start Here — New to GLP-1?", href: "/start-here", highlight: true },
    { name: "Articles", href: "/glp-guide" },
    { name: "Dose Library", href: "/dose-and-meds" },
    { name: "Kai AI Coach", href: "/meet-kai" },
    { name: "Pricing", href: "/pricing" },
  ];

  const freeTools = [
    { name: "Nausea Fix", href: "/glp-nausea-fix" },
    { name: "Constipation Fix", href: "/glp-constipation-fix" },
    { name: "Energy & Plateau", href: "/glp-energy-plateau" },
    { name: "Muscle Loss Risk Check", href: "/glp-muscle-loss" },
    { name: "Plateau Detector", href: "/glp-plateau-detector" },
    { name: "GLP-1 Level Calculator", href: "/glp-pk-calculator" },
    { name: "GLP-1: 101 Protocols", href: "/glp-guide" },
    { name: "GLP-1 Readiness Checklist", href: "/free/glp-1-readiness-checklist" },
    { name: "Quick Fix Cheat Sheet", href: "/free/quick-fix-cheat-sheet" },
    { name: "7-Day Starter Checklist", href: "/free/7-day-starter-checklist" },
  ];

  const communityHref = "https://www.skool.com/glp-1-lab-os-1374";

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <header className="nav-glass fixed top-0 left-0 right-0 z-50">
        <nav className="container-main">
          <div
            className="grid grid-cols-[auto_1fr_auto] md:grid-cols-3 items-center h-16 md:h-20 gap-2"
          >
            {/* Logo — LEFT column */}
            <Link to="/" className="flex items-center gap-2 justify-self-start" data-testid="logo-link">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2D6A4F] to-[#1B4332] flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-lg text-[#1A2018] hidden sm:block">MetabolicLabOS</span>
            </Link>

            {/* CENTER column — Start the System CTA (desktop only). Dead-center via grid */}
            <div className="hidden md:flex justify-center">
              <Link
                to="/meet-tracker"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#2D6A4F] text-white hover:bg-[#1B4332] whitespace-nowrap transition-colors"
                data-testid="nav-start-the-system"
              >
                <ClipboardList className="w-4 h-4" />
                Start the System
              </Link>
            </div>

            {/* RIGHT column — Resources / Community / Sign In (desktop) + hamburger (mobile) */}
            <div className="flex items-center gap-6 justify-self-end">
              <div className="hidden md:flex items-center gap-6">
                {/* Resources dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setResourcesDropdownOpen(true)}
                  onMouseLeave={() => setResourcesDropdownOpen(false)}
                >
                  <button
                    className="flex items-center gap-1 text-sm font-medium text-[#4B5548] hover:text-[#1A2018] transition-colors"
                    data-testid="nav-resources-btn"
                    aria-expanded={resourcesDropdownOpen}
                  >
                    Resources
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <AnimatePresence>
                    {resourcesDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
                        data-testid="nav-resources-dropdown"
                      >
                        <div className="p-2">
                          {resourcesPrimary.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={
                                item.highlight
                                  ? "block px-3 py-2 mb-1 text-sm font-semibold text-[#2D6A4F] bg-[#E9EDC9]/60 hover:bg-[#E9EDC9] rounded-lg transition-colors"
                                  : "block px-3 py-2 text-sm text-[#4B5548] hover:bg-[#F2F0E9] rounded-lg transition-colors"
                              }
                              data-testid={`resources-link-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                            >
                              {item.name}
                            </Link>
                          ))}
                          <div className="border-t border-gray-100 my-2" />
                          <p className="px-3 py-2 text-xs font-semibold text-[#838F80] uppercase tracking-wide">
                            Free Tools
                          </p>
                          {freeTools.map((tool) => (
                            <Link
                              key={tool.name}
                              to={tool.href}
                              className="block px-3 py-2 text-sm text-[#4B5548] hover:bg-[#F2F0E9] rounded-lg transition-colors"
                              data-testid={`resources-tool-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {tool.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <a
                  href={communityHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-[#4B5548] hover:text-[#1A2018] transition-colors"
                  data-testid="nav-community"
                >
                  Community
                </a>

                <Link
                  to="/tracker/login"
                  className="text-sm font-medium text-[#4B5548] hover:text-[#1A2018] transition-colors"
                  data-testid="nav-sign-in"
                >
                  Sign In
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu — same 5 sections */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-gray-100"
              data-testid="mobile-menu"
            >
              <div className="container-main py-4 space-y-2">
                <Link
                  to="/meet-tracker"
                  className="block w-full text-center py-3 rounded-full bg-[#2D6A4F] text-white font-semibold"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-start-the-system"
                >
                  Start the System
                </Link>

                <div className="border-t border-gray-100 my-2 pt-2">
                  <p className="px-4 py-2 text-xs font-semibold text-[#838F80] uppercase">Resources</p>
                  {resourcesPrimary.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={
                        item.highlight
                          ? "block px-4 py-3 text-base font-semibold text-[#2D6A4F] bg-[#E9EDC9]/60 hover:bg-[#E9EDC9] rounded-lg transition-colors mb-1"
                          : "block px-4 py-3 text-base text-[#4B5548] hover:bg-[#F2F0E9] rounded-lg transition-colors"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                <div className="border-t border-gray-100 my-2 pt-2">
                  <p className="px-4 py-2 text-xs font-semibold text-[#838F80] uppercase">Free Tools</p>
                  {freeTools.map((tool) => (
                    <Link
                      key={tool.name}
                      to={tool.href}
                      className="block px-4 py-3 text-base text-[#4B5548] hover:bg-[#F2F0E9] rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {tool.name}
                    </Link>
                  ))}
                </div>

                <div className="border-t border-gray-100 my-2 pt-2">
                  <a
                    href={communityHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-3 text-base font-medium text-[#4B5548] hover:bg-[#F2F0E9] rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Community
                  </a>
                  <Link
                    to="/tracker/login"
                    className="block px-4 py-3 text-base font-medium text-[#4B5548] hover:bg-[#F2F0E9] rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-16 md:pt-20">
        <Outlet />
      </main>

      {/* Exit Intent Popup */}
      <ExitIntentPopup />

      {/* Floating Kai launcher — hidden on /kai and /admin routes */}
      <KaiLauncher />

      {/* Footer */}
      <footer className="bg-[#1A2018] text-white mt-auto dark-section">
        <div className="container-main py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to="/admin/login" className="flex items-center gap-2 mb-4 cursor-default" data-testid="footer-logo-link">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2D6A4F] to-[#1B4332] flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold text-lg">MetabolicLabOS</span>
              </Link>
              <p className="text-sm text-gray-400 leading-relaxed">
                Educational resources for GLP-1 users. Helping you build better routines with structured systems and practical guides.
              </p>
            </div>

            {/* Products */}
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide text-gray-400">Products</h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/beta-test"
                    data-testid="footer-beta-link"
                    className="text-sm text-gray-300 hover:text-white transition-colors inline-flex items-center gap-1.5"
                  >
                    <UserCheck className="w-3 h-3 text-[#C8614A]" /> Beta Test (25 Spots)
                  </Link>
                </li>
                <li>
                  <Link to="/meet-tracker" className="text-sm text-gray-300 hover:text-white transition-colors inline-flex items-center gap-1.5">
                    <ClipboardList className="w-3 h-3 text-[#C8614A]" /> Tracker
                  </Link>
                </li>
                <li><Link to="/glp-survival-system" className="text-sm text-gray-300 hover:text-white transition-colors">GLP Survival System</Link></li>
                <li><Link to="/guides/first-30-days" className="text-sm text-gray-300 hover:text-white transition-colors">First 30 Days Guide</Link></li>
                <li><Link to="/guides/fix-side-effects-7-days" className="text-sm text-gray-300 hover:text-white transition-colors">Fix Side Effects Guide</Link></li>
                <li><Link to="/account" className="text-sm text-gray-300 hover:text-white transition-colors">My Purchases</Link></li>
              </ul>
            </div>

            {/* Free Tools */}
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide text-gray-400">Free Tools</h4>
              <ul className="space-y-3">
                <li><Link to="/glp-nausea-fix" className="text-sm text-gray-300 hover:text-white transition-colors">Nausea Fix Tool</Link></li>
                <li><Link to="/glp-constipation-fix" className="text-sm text-gray-300 hover:text-white transition-colors">Constipation Fix Tool</Link></li>
                <li><Link to="/glp-energy-plateau" className="text-sm text-gray-300 hover:text-white transition-colors">Energy & Plateau Tool</Link></li>
                <li><Link to="/glp-muscle-loss" className="text-sm text-gray-300 hover:text-white transition-colors">Muscle Loss Risk Check</Link></li>
                <li><Link to="/glp-plateau-detector" className="text-sm text-gray-300 hover:text-white transition-colors">Plateau Detector</Link></li>
                <li><Link to="/glp-pk-calculator" className="text-sm text-gray-300 hover:text-white transition-colors">GLP-1 Level Calculator</Link></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide text-gray-400">Resources</h4>
              <ul className="space-y-3">
                <li><Link to="/meet-kai" className="text-sm text-gray-300 hover:text-white transition-colors inline-flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-[#C8614A]" /> Kai AI Coach
                </Link></li>
                <li><Link to="/pricing" className="text-sm text-gray-300 hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/free-guide" className="text-sm text-gray-300 hover:text-white transition-colors">GLP-1: 101 Protocols (Free)</Link></li>
                <li><Link to="/free/glp-1-readiness-checklist" className="text-sm text-gray-300 hover:text-white transition-colors">GLP-1 Readiness Checklist</Link></li>
                <li><Link to="/free/quick-fix-cheat-sheet" className="text-sm text-gray-300 hover:text-white transition-colors">Free Cheat Sheet</Link></li>
                <li><Link to="/free/7-day-starter-checklist" className="text-sm text-gray-300 hover:text-white transition-colors">Free Starter Checklist</Link></li>
                <li><Link to="/glp-guide" className="text-sm text-gray-300 hover:text-white transition-colors">Resource Hub</Link></li>
                <li><Link to="/dose-and-meds" className="text-sm text-gray-300 hover:text-white transition-colors">Dose &amp; Med Library</Link></li>
                <li><Link to="/faq" className="text-sm text-gray-300 hover:text-white transition-colors">FAQ</Link></li>
                <li><a href="https://www.skool.com/glp-1-lab-os-1374" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-white transition-colors">Community (Skool)</a></li>
              </ul>
            </div>
          </div>

          {/* Legal Links */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-wrap justify-center gap-6 mb-6">
              <Link to="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">Terms of Use</Link>
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/disclaimer" className="text-sm text-gray-400 hover:text-white transition-colors">Medical Disclaimer</Link>
              <Link to="/refund" className="text-sm text-gray-400 hover:text-white transition-colors">Refund Policy</Link>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div>
                <p
                  className="text-xs text-gray-400 italic mb-1"
                  data-testid="footer-positioning-line"
                >
                  The support system for everything that happens after the prescription.
                </p>
                <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} MetabolicLabOS. All rights reserved.</p>
              </div>
              <p className="text-xs text-gray-600 text-center md:text-right max-w-md">
                For educational purposes only. Not medical advice. Always consult your healthcare provider before making health-related decisions. Individual results vary.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
